import { useEffect, useRef, useState } from "react";
import { Stack, Typography, Button } from "@mui/material";
import { useFormContext, Controller } from "react-hook-form";
import { Icon } from "@iconify/react";

type Props = {
  name: string;
  label: string;
  width?: number | string;
  height?: number | string;
  placeholder?: string;
};

export function RHFUploadImage({
  name,
  label,
  width = 250,
  height = 150,
  placeholder = "Click to upload photo",
}: Props) {
  const { control, setValue } = useFormContext();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => {
        const openFileDialog = () => inputRef.current?.click();

        const onFileChange: React.ChangeEventHandler<HTMLInputElement> = (
          e
        ) => {
          const file = e.target.files?.[0];
          if (!file) return;
          if (!file.type.startsWith("image/")) return;

          const url = URL.createObjectURL(file);
          setPreviewUrl(url);
          setValue(name, file, { shouldValidate: true });
          field.onChange(file);
        };

        const clearPhoto = () => {
          if (previewUrl) URL.revokeObjectURL(previewUrl);
          setPreviewUrl(null);
          setValue(name, null, { shouldValidate: true });
          if (inputRef.current) inputRef.current.value = "";
        };

        return (
          <Stack sx={{ flexDirection: "column" }}>
            <input
              ref={inputRef}
              type="file"
              accept="image/*"
              hidden
              onChange={onFileChange}
            />

            <Stack
              onClick={openFileDialog}
              role="button"
              tabIndex={0}
              sx={{
                width,
                height,
                border: (theme) => `2px dashed ${theme.palette.divider}`,
                borderRadius: 2,
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                overflow: "hidden",
                position: "relative",
                backgroundImage: previewUrl ? `url(${previewUrl})` : "none",
                backgroundSize: "cover",
                backgroundPosition: "center",
                bgcolor: (theme) =>
                  previewUrl ? "transparent" : theme.palette.background.paper,
              }}
            >
              {!previewUrl && (
                <Typography variant="body2" color="text.secondary">
                  {placeholder}
                </Typography>
              )}

              {previewUrl && (
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{ position: "absolute", right: 8, top: 8 }}
                >
                  <Button
                    size="small"
                    variant="contained"
                    onClick={(e) => {
                      e.stopPropagation();
                      openFileDialog();
                    }}
                  >
                    <Icon icon={"material-symbols:change-circle-outline"} />
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={(e) => {
                      e.stopPropagation();
                      clearPhoto();
                    }}
                  >
                    <Icon icon={"material-symbols:delete-outline-rounded"} />
                  </Button>
                </Stack>
              )}
            </Stack>
            <Typography variant="subtitle1" textAlign={"center"}>
              {label}
            </Typography>
          </Stack>
        );
      }}
    />
  );
}
